package com.schoolconnect.app.feature.itusers

import com.schoolconnect.app.feature.itusers.add.AddITUserModal
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.sp
import com.schoolconnect.app.designsystem.AppConfirmationDialog
import com.schoolconnect.app.designsystem.AppDanger
import com.schoolconnect.app.designsystem.AppPageHeader
import com.schoolconnect.app.designsystem.AppPageSubtitle
import com.schoolconnect.app.designsystem.AppConfirmationDialogTone
import com.schoolconnect.app.designsystem.AppSuccess
import ischoolconnect.sharedui.generated.resources.Res
import ischoolconnect.sharedui.generated.resources.circle_check
import ischoolconnect.sharedui.generated.resources.trash
import org.jetbrains.compose.resources.painterResource
import com.schoolconnect.app.designsystem.list.AdminListFilterBanner
import com.schoolconnect.app.designsystem.list.AdminInfoBanner
import com.schoolconnect.app.designsystem.list.AdminListPageScaffold
import com.schoolconnect.app.designsystem.list.AdminListPrimaryActionButton
import com.schoolconnect.app.feature.common.footerText
import com.schoolconnect.app.feature.common.ShellTopRightActions
import com.schoolconnect.app.feature.common.shellAvatarInitials
import com.schoolconnect.app.feature.common.shellRoleLabel
import com.schoolconnect.app.feature.common.shellRoleSubtitle
import com.schoolconnect.app.feature.common.toWindow
import androidx.compose.ui.Alignment
import androidx.compose.ui.text.SpanStyle
import androidx.compose.ui.text.buildAnnotatedString
import androidx.compose.ui.text.withStyle
import ischoolconnect.sharedui.generated.resources.plus

@Composable
fun ITUsersScreen(
    state: ITUsersUiState,
    onEvent: (ITUsersUiEvent) -> Unit,
    onBack: () -> Unit,
    shellRoleCode: String = "",
    shellSchoolLabel: String = "iConnect.School",
    onChangeRole: () -> Unit = onBack,
    onLogout: () -> Unit = {}
) {
    val pageItems = state.users
    val pageWindow = state.paging.toWindow(pageItems.size)
    val pageText = state.paging.footerText(pageItems.size, "users")
    var openActionMenuUserId by remember { mutableStateOf<String?>(null) }
    var showFilterModal by remember { mutableStateOf(false) }
    var showViewModal by remember { mutableStateOf(false) }
    var showEditorModal by remember { mutableStateOf(false) }
    var viewingUserId by remember { mutableStateOf<String?>(null) }
    var editorUserId by remember { mutableStateOf<String?>(null) }
    var editorInitialValues by remember { mutableStateOf(ITUserFormValues()) }
    var pendingUserAction by remember { mutableStateOf<PendingITUserAction?>(null) }
    var rowsPerPageExpanded by remember { mutableStateOf(false) }
    var accountMenuExpanded by remember { mutableStateOf(false) }
    val shellRoleLabelText = shellRoleLabel(shellRoleCode)
    val roleSubtitle = shellRoleSubtitle(shellRoleCode)
    val initials = shellAvatarInitials(shellRoleLabelText)

    LaunchedEffect(pageItems) {
        if (openActionMenuUserId != null && pageItems.none { it.id == openActionMenuUserId }) {
            openActionMenuUserId = null
        }
    }

    LaunchedEffect(state.editSaveCompletedNonce) {
        if (state.editSaveCompletedNonce > 0) {
            showEditorModal = false
            editorUserId = null
        }
    }

    val currentFilterValues = ITUsersFilterFormValues(
        searchQuery = state.searchQuery,
        role = state.roleFilter,
        status = state.statusFilter,
        createdFrom = state.createdFrom,
        createdTo = state.createdTo
    )
    val activeFilterSummary = remember(state) { itUsersFilterLabels(state) }
    val analytics = state.analytics?.metrics
    val activeUsers = analytics?.activeUsers
        ?: pageItems.count { it.status == ITUserStatusUi.ACTIVE }
    val inactiveUsers = analytics?.inactiveUsers
        ?: pageItems.count { it.status != ITUserStatusUi.ACTIVE }
    val itUsersSubtitle = remember(activeUsers, inactiveUsers) {
        buildAnnotatedString {
            append("Manage platform support staff, developers, DevOps and internal operations. ")
            withStyle(SpanStyle(color = AppSuccess, fontWeight = FontWeight.Bold)) {
                append("$activeUsers Active")
            }
            append(" and ")
            withStyle(SpanStyle(color = AppDanger, fontWeight = FontWeight.Bold)) {
                append("$inactiveUsers Inactive")
            }
            append(" users.")
        }
    }

    Box(modifier = Modifier.fillMaxSize()) {
        AdminListPageScaffold(
            title = "IT Support Users",
            subtitle = "Manage platform support staff, developers, DevOps and internal operations.",
            subtitleAnnotated = itUsersSubtitle,
            breadcrumb = listOf("Platform Operations", "IT Support Users"),
            showSummaryCards = false,
            headerActions = {
                ShellTopRightActions(
                    roleLabel = shellRoleLabelText,
                    roleSubtitle = roleSubtitle,
                    initials = initials,
                    expanded = accountMenuExpanded,
                    onExpandedChange = { accountMenuExpanded = it },
                    onChangeRole = onChangeRole,
                    onLogout = onLogout
                )
            },
            backgroundColor = ITUsersBackground,
            titleColor = AppPageHeader,
            subtitleColor = AppPageSubtitle,
            maxContentWidth = 1216.dp,
            summaryCards = {},
            banner = {
                val userDialogOpen = state.isAddUserModalOpen || showEditorModal
                if (!userDialogOpen && (state.errorMessage.isNotBlank() || state.infoMessage.isNotBlank() || activeFilterSummary.isNotEmpty())) {
                    Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
                        if (state.errorMessage.isNotBlank() || state.infoMessage.isNotBlank()) {
                            AdminInfoBanner(
                                message = state.errorMessage.ifBlank { state.infoMessage },
                                onDismiss = { onEvent(ITUsersUiEvent.ClearMessages) },
                                leadingIcon = painterResource(Res.drawable.circle_check),
                                backgroundColor = Color(0xFFF3F7FF),
                                borderColor = Color(0xFFD8E4FF),
                                contentColor = ITUsersPrimary,
                                accentColor = ITUsersPrimary,
                                isError = state.errorMessage.isNotBlank()
                            )
                        }
                        AdminListFilterBanner(
                            chips = activeFilterSummary,
                            onClearAll = { onEvent(ITUsersUiEvent.FiltersReset) },
                            onRemoveChip = { chip ->
                                val next = when {
                                    chip.startsWith("Search:") -> currentFilterValues.copy(searchQuery = "")
                                    chip.startsWith("Created:") -> currentFilterValues.copy(createdFrom = "", createdTo = "")
                                    else -> currentFilterValues
                                }
                                onEvent(ITUsersUiEvent.FiltersApplied(next))
                            },
                            onEditFilters = { showFilterModal = true },
                            backgroundColor = Color(0xFFF2F7FF),
                            borderColor = ITUsersBorder,
                            contentColor = ITUsersNavy,
                            accentColor = ITUsersPrimary
                        )
                    }
                }
            },
            toolbar = {
                ITUsersToolbar(
                    searchQuery = state.searchQuery,
                    onSearchQueryChanged = { onEvent(ITUsersUiEvent.SearchChanged(it)) },
                    roleFilter = state.roleFilter,
                    statusFilter = state.statusFilter,
                    onRoleFilterChanged = { role ->
                        onEvent(
                            ITUsersUiEvent.FiltersApplied(
                                currentFilterValues.copy(role = role)
                            )
                        )
                    },
                    onStatusFilterChanged = { status ->
                        onEvent(
                            ITUsersUiEvent.FiltersApplied(
                                currentFilterValues.copy(status = status)
                            )
                        )
                    },
                    onResetClick = { onEvent(ITUsersUiEvent.FiltersReset) },
                    onFilterClick = { showFilterModal = true }
                )
            },
            table = { tableModifier ->
                ITUsersTableCard(
                    modifier = tableModifier,
                    sortField = state.sortField,
                    sortAscending = state.sortAscending,
                    items = pageItems,
                    isLoading = state.isLoading,
                    pageText = pageText,
                    pageIndex = state.paging.pageIndex,
                    pageCount = pageWindow.pageCount,
                    pageSize = state.paging.pageSize,
                    openActionMenuUserId = openActionMenuUserId,
                    onToggleActionMenu = { userId ->
                        openActionMenuUserId = if (openActionMenuUserId == userId) null else userId
                    },
                    onCloseActionMenu = { userId ->
                        if (openActionMenuUserId == userId) openActionMenuUserId = null
                    },
                    onSortChanged = { onEvent(ITUsersUiEvent.SortChanged(it)) },
                    onViewUser = { userId ->
                        openActionMenuUserId = null
                        viewingUserId = userId
                        showViewModal = true
                    },
                    onEditUser = { userId ->
                        openActionMenuUserId = null
                        onEvent(ITUsersUiEvent.ClearMessages)
                        val user = state.users.firstOrNull { it.id == userId }
                        if (user != null) {
                            editorUserId = user.id
                            editorInitialValues = ITUserFormValues(
                                name = user.name,
                                mobile = user.mobile,
                                email = user.email,
                                role = roleLabel(user.role),
                                status = statusLabel(user.status)
                            )
                            showEditorModal = true
                        }
                    },
                    onToggleStatus = { userId ->
                        openActionMenuUserId = null
                        val user = state.users.firstOrNull { it.id == userId }
                        if (user != null) {
                            pendingUserAction = PendingITUserAction(user, PendingActionType.ToggleStatus)
                        }
                    },
                    onDeleteUser = { userId ->
                        openActionMenuUserId = null
                        val user = state.users.firstOrNull { it.id == userId }
                        if (user != null) {
                            pendingUserAction = PendingITUserAction(user, PendingActionType.Delete)
                        }
                    },
                    rowsPerPageExpanded = rowsPerPageExpanded,
                    onRowsPerPageToggle = { rowsPerPageExpanded = !rowsPerPageExpanded },
                    onRowsPerPageDismiss = { rowsPerPageExpanded = false },
                    onPageSizeChanged = { onEvent(ITUsersUiEvent.PageSizeChanged(it)) },
                    onPageChanged = { onEvent(ITUsersUiEvent.PageChanged(it)) }
                )
            },
            bottomAction = {
                AdminListPrimaryActionButton(
                    label = "Add IT User",
                    icon = painterResource(Res.drawable.plus),
                    onClick = {
                        onEvent(ITUsersUiEvent.AddUserClicked)
                    }
                )
            }
        )

        ITUsersFilterModal(
            open = showFilterModal,
            initialValues = currentFilterValues,
            onClose = { showFilterModal = false },
            onApply = { values ->
                onEvent(
                    ITUsersUiEvent.FiltersApplied(
                        currentFilterValues.copy(
                            role = values.role,
                            status = values.status,
                            createdFrom = values.createdFrom,
                            createdTo = values.createdTo
                        )
                    )
                )
            },
            onResetModalFilters = {
                onEvent(
                    ITUsersUiEvent.FiltersApplied(
                        currentFilterValues.copy(
                            role = "",
                            status = "",
                            createdFrom = "",
                            createdTo = ""
                        )
                    )
                )
            }
        )

        if (showViewModal) {
            val viewUser = viewingUserId?.let { id -> state.users.firstOrNull { it.id == id } }
            if (viewUser != null) {
                ITUsersViewModal(
                    open = true,
                    user = viewUser,
                    onClose = {
                        showViewModal = false
                        viewingUserId = null
                    }
                )
            }
        }

        if (state.isAddUserModalOpen) {
            AddITUserModal(
                open = true,
                title = "Add IT User",
                subtitle = "Create a new IT user for platform access.",
                initialValues = ITUserFormValues(),
                onClose = { onEvent(ITUsersUiEvent.AddUserModalClosed) },
                onSave = { values ->
                    onEvent(ITUsersUiEvent.SaveUser(null, values))
                },
                saveLabel = "Save",
                isSaving = state.isAddUserSaving,
                apiError = state.errorMessage
            )
        }

        if (showEditorModal && editorUserId != null) {
            val selectedUser = editorUserId?.let { id -> state.users.firstOrNull { it.id == id } }
            EditITUserModal(
                open = true,
                title = "Edit IT User",
                subtitle = "Update IT user information and access.",
                initialValues = editorInitialValues,
                onClose = { if (!state.isEditUserSaving) showEditorModal = false },
                onSave = { values ->
                    onEvent(ITUsersUiEvent.SaveUser(editorUserId, values))
                },
                saveLabel = "Save",
                isSaving = state.isEditUserSaving,
                apiError = state.errorMessage
            )
        }

        pendingUserAction?.let { pending ->
            val user = pending.user
            val isActivate = user.status != ITUserStatusUi.ACTIVE
            val actionLabel = if (isActivate) "Activate" else "Inactivate"
            AppConfirmationDialog(
                onDismiss = { pendingUserAction = null },
                title = when (pending.action) {
                    PendingActionType.Delete -> "Delete IT User"
                    PendingActionType.ToggleStatus -> "$actionLabel IT User"
                },
                subtitle = when (pending.action) {
                    PendingActionType.Delete -> "This will move the IT user to inactive records."
                    PendingActionType.ToggleStatus -> if (isActivate) {
                        "This will restore the IT user to active records."
                    } else {
                        "This will move the IT user to inactive records."
                    }
                },
                bodyText = when (pending.action) {
                    PendingActionType.Delete -> "Delete ${user.name}?"
                    PendingActionType.ToggleStatus -> "$actionLabel ${user.name}?"
                },
                confirmLabel = when (pending.action) {
                    PendingActionType.Delete -> "Delete"
                    PendingActionType.ToggleStatus -> actionLabel
                },
                onConfirm = {
                    when (pending.action) {
                        PendingActionType.Delete -> onEvent(ITUsersUiEvent.DeleteUser(user.id))
                        PendingActionType.ToggleStatus -> onEvent(ITUsersUiEvent.ToggleStatus(user.id))
                    }
                    pendingUserAction = null
                },
                tone = if (pending.action == PendingActionType.Delete) {
                    AppConfirmationDialogTone.Danger
                } else {
                    AppConfirmationDialogTone.Primary
                }
            )
        }
    }
}

private enum class PendingActionType {
    Delete,
    ToggleStatus
}

private data class PendingITUserAction(
    val user: ITUserRowUi,
    val action: PendingActionType
)
